import { T } from '@transifex/react';
import { MediaObjectLink } from 'features/common/components/MediaObjectLink';
import { Box, Heading, Skeleton, VStack } from '@chakra-ui/react';
import { useSupportCase } from 'features/snow/hooks/useSupportCase';
import { useDrawerStateDispatch, useMainState } from 'features/main/context';
import {
  Action,
  useGetGoLiveDateQuery,
  useGetQuickLinksQuery,
} from 'gql/graphql';
import { quickActionAdapter } from 'features/domains/portal/content/utils';
import { useHistory } from 'react-router';
import { useFeature, useFeatures } from 'lib/featureFlags/features';
import { isFeatureFlagEnabled } from 'features/domains/portal/content/helpLinks';
import { xmCloudDeployActionVar } from 'lib/@apollo/client/ApolloProvider';
import { useAppAccess } from 'features/common/components/OrganizationAppAccess/utils';
import { filterQuickLinksByRole } from 'features/domains/portal/content/utils/quickLinkAccessControl';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { useEffect, useMemo } from 'react';
import { mdiRocketLaunchOutline } from '@mdi/js';

const EMPTY_QUICK_LINKS: Action[] = [];

const QuickLinksPanel: React.FC = () => {
  const state = useMainState();
  const { data, loading } = useGetQuickLinksQuery();
  const quickLinks =
    data?.organization?.appearance?.web?.actions?.nodes ?? EMPTY_QUICK_LINKS;
  const [, drawerDispatch] = useDrawerStateDispatch();
  const history = useHistory();
  const features = useFeatures();
  const { hasOrgAdminOwnerAccess } = useAppAccess();

  const shouldShowSnow = isFeatureFlagEnabled('SNOW', features);
  const goLivePlanningEnabled = useFeature('GO_LIVE_PLANNING');
  const { data: goLiveDateData, error: goLiveError } = useGetGoLiveDateQuery({
    skip: !goLivePlanningEnabled,
  });
  const goLiveDate = goLiveDateData?.serviceNow?.goLiveDate;
  const showGoLiveDate = !goLiveDate && !goLiveError;

  const featureFlagFilteredLinks = useMemo(() => {
    let links = quickLinks;

    if (!shouldShowSnow) {
      links = links.filter(({ link }) => link?.to !== 'create-ticket-drawer');
    }

    if (!goLivePlanningEnabled || !showGoLiveDate) {
      links = links.filter(({ link }) => link?.to !== 'go-live-date-drawer');
    }

    return links;
  }, [quickLinks, shouldShowSnow, goLivePlanningEnabled, showGoLiveDate]);

  const filteredLinks = useMemo(() => {
    const roleFiltered = filterQuickLinksByRole(
      featureFlagFilteredLinks,
      hasOrgAdminOwnerAccess,
    );

    return roleFiltered.filter((link) => {
      if (
        link.name === 'SitecoreAI Deploy' ||
        link.name === 'XM Cloud Deploy'
      ) {
        return !!state?.env?.WITH_XM_CLOUD_CONTEXT;
      }
      return true;
    });
  }, [
    featureFlagFilteredLinks,
    hasOrgAdminOwnerAccess,
    state?.env?.WITH_XM_CLOUD_CONTEXT,
  ]);

  const xmCloudDeployAction = useMemo(
    () =>
      filteredLinks.find(
        (quickAction) => quickAction.name === 'SitecoreAI Deploy',
      ),
    [filteredLinks],
  );

  useEffect(() => {
    const nextAction = xmCloudDeployAction ?? null;

    if (xmCloudDeployActionVar() !== nextAction) {
      xmCloudDeployActionVar(nextAction);
    }
  }, [xmCloudDeployAction]);

  useSupportCase({
    componentName: 'create-support-case-drawer',
    buttonSelector: '[data-support-case="create-support-case-drawer"]',
  });

  return (
    <Box
      w='full'
      data-testid='quick-links-panel'
      data-behavior-analytics-feature='quick-links - home page'
    >
      <Heading variant='section' pb='4'>
        <T _str='Quick Links' />
      </Heading>
      {loading ? (
        <VStack>
          <Skeleton w='full' h={12} />
          <Skeleton w='full' h={12} />
          <Skeleton w='full' h={12} />
          <Skeleton w='full' h={12} />
        </VStack>
      ) : (
        <VStack>
          {filteredLinks.map((quickAction) => {
            const {
              iconColor,
              action,
              title,
              description,
              svgIcon,
              iconPath,
              link,
            } = quickActionAdapter(quickAction, {
              dispatch: drawerDispatch,
              history,
            });

            return (
              <MediaObjectLink
                onClick={() => {
                  if (link === 'create-ticket-drawer') {
                    return () => void 0;
                  }

                  if (link === 'go-live-date-drawer') {
                    return drawerDispatch(openDrawer('go-live-date-drawer'));
                  }

                  return action();
                }}
                title={title}
                link={link}
                key={title}
                data-testid={`help-item-${title}`}
                iconPath={
                  iconPath === undefined && link === 'go-live-date-drawer'
                    ? mdiRocketLaunchOutline
                    : iconPath
                }
                iconColor={iconColor}
                svgIcon={svgIcon}
                description={description}
                {...(link === 'create-ticket-drawer'
                  ? {
                      'data-support-case': 'create-support-case-drawer',
                    }
                  : {})}
              />
            );
          })}
        </VStack>
      )}
      {state.env?.ORG_ADMIN_OR_OWNER && shouldShowSnow && (
        <create-support-case-drawer />
      )}
    </Box>
  );
};

export default QuickLinksPanel;
